import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import { useTranslation } from 'react-i18next';
import type { EmergencyItem } from '../apis/emergencyService';

const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 360,
    maxWidth: '90vw',
    bgcolor: 'background.paper',
    border: '0px solid #000',
    boxShadow: 24,
    p: 2,
};

const NHI_QUERY_URL = 'https://info.nhi.gov.tw/INAE4000/INAE4001S01';

export interface EmergencyModalProps {
    title: string;
    data: EmergencyItem;
    updatedAt?: string;
}

const fmt = (v: number | null) => (v === null || v === undefined ? '-' : String(v));

const rowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '4px 0',
};

const EmergencyModal = ({ title, data, updatedAt }: EmergencyModalProps) => {
    const { t } = useTranslation("clinic");
    const [open, setOpen] = React.useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    return (
        <div>
            <Button onClick={handleOpen} style={{ color: '#0000ff', fontSize: '18px' }}>
                {t("emergency.overview")}
            </Button>
            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="emergency-modal-title"
                aria-describedby="emergency-modal-description"
            >
                <Box sx={style}>
                    <Typography id="emergency-modal-title" variant="h6" component="h2">
                        {title}
                    </Typography>
                    {/* 直向逐項顯示，避免橫向不易讀 */}
                    <div
                        id="emergency-modal-description"
                        style={{ marginTop: '12px', color: '#555555', fontSize: '16px' }}
                    >
                        <div style={{ fontWeight: 700, marginBottom: '4px' }}>{t("emergency.waitCount")}</div>
                        <div style={rowStyle}><span>{t("emergency.inpatient")}</span><span>{fmt(data.waitBedCount)}</span></div>
                        <div style={rowStyle}><span>{t("emergency.seeDoctor")}</span><span>{fmt(data.waitSeeCount)}</span></div>
                        <div style={rowStyle}><span>{t("emergency.bed")}</span><span>{fmt(data.waitGeneralCount)}</span></div>
                        <div style={rowStyle}><span>{t("emergency.icu")}</span><span>{fmt(data.waitIcuCount)}</span></div>
                        <div
                            style={{
                                ...rowStyle,
                                borderTop: '1px solid #eee',
                                marginTop: '4px',
                                paddingTop: '8px',
                            }}
                        >
                            <span>{t("emergency.inform119")}</span>
                            <span>{data.inform119Full === 'Y' ? t("emergency.yes") : t("emergency.no")}</span>
                        </div>
                    </div>

                    {(updatedAt || data.textUpdatedAt) && (
                        <div style={{ marginTop: '10px', color: '#999999', fontSize: '12px' }}>
                            {t("emergency.updatedAt")}{updatedAt || data.textUpdatedAt}
                        </div>
                    )}

                    {/* 警語 + 官方連結（該筆有 sourceUrl 用其來源，否則預設衛福部） */}
                    <div
                        style={{
                            marginTop: '12px',
                            color: '#999999',
                            fontSize: '12px',
                            lineHeight: 1.5,
                        }}
                    >
                        {t("emergency.disclaimer1")}
                        <br />
                        {t("emergency.seeMore")}
                        <a
                            href={data.sourceUrl || NHI_QUERY_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#0000ff', marginLeft: '4px' }}
                        >
                            {data.sourceLabel || t("emergency.defaultSource")}
                        </a>
                    </div>
                </Box>
            </Modal>
        </div>
    );
};

export default EmergencyModal;
